import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import useLanguage from '@/components/common/hooks/useLanguage';
import RequisiteForm from '@/components/payout/RequisiteForm';
import { RequisitePayee } from '@/store/actions/payout';
import { PaymentRequisite } from '@/store/reducers/payout';

interface Props {
  payee: RequisitePayee;
  // Null when nothing is on file yet, which is most people to begin with.
  requisite: PaymentRequisite | null;
}

/**
 * Where a payee's money goes: the four fields of an IBAN transfer, each with its
 * own copy button, because they go into four separate boxes in a bank app.
 */
const Requisites: React.FC<Props> = ({ payee, requisite }) => {
  const [{ payout }] = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // The IBAN goes without its grouping spaces, which some bank apps reject;
  // everything else is copied as typed.
  const copy = (field: string, value: string) => {
    if (!navigator?.clipboard) return;

    navigator.clipboard.writeText(field === 'iban' ? value.replace(/\s+/g, '') : value)
      .then(() => {
        setCopied(field);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(null), 1500);
      })
      .catch(() => undefined);
  };

  const rows: { field: string, label: string, value?: string }[] = requisite ? [
    { field: 'name', label: payout.requisite_name, value: requisite.name },
    { field: 'iban', label: payout.requisite_iban, value: requisite.iban },
    { field: 'swift', label: payout.requisite_swift, value: requisite.swift },
    { field: 'country', label: payout.requisite_country, value: requisite.country },
  ] : [];

  return (
    <>
      <SectionHead>
        <Label>{payout.payment_details}</Label>
        <EditLink onClick={() => setIsEditing(true)}>
          {requisite ? payout.edit : payout.add}
        </EditLink>
      </SectionHead>

      {!requisite && <Empty>{payout.no_payment_details}</Empty>}
      {rows.map(({ field, label, value }) => (
        <Row key={field}>
          <RowLabel>{label}</RowLabel>
          {value ? (
            <>
              <Value isMono={field === 'iban' || field === 'swift'}>{value}</Value>
              <CopyLink onClick={() => copy(field, value)}>
                {copied === field ? payout.copied : payout.copy}
              </CopyLink>
            </>
          ) : (
            <Missing>—</Missing>
          )}
        </Row>
      ))}

      {isEditing && (
        <RequisiteForm
          payee={payee}
          requisite={requisite}
          onClose={() => setIsEditing(false)}
        />
      )}
    </>
  );
};

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 22px 0 4px;
`;

const Label = styled.div`
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.2px;
  color: #909599;
`;

const EditLink = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #fb7e14;
  cursor: pointer;
`;

const Empty = styled.div`
  font-size: 14px;
  color: #909599;
  padding: 10px 0;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: solid 1px #f4f6f8;
`;

const RowLabel = styled.div`
  flex-shrink: 0;
  width: 72px;
  font-size: 12px;
  color: #909599;
`;

const Value = styled.div<{ isMono: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #21272e;
  word-break: break-all;
  font-family: ${({ isMono }) => (isMono ? "'SFMono-Regular', Menlo, Consolas, monospace" : 'inherit')};
`;

const Missing = styled.div`
  flex: 1;
  font-size: 14px;
  color: #909599;
`;

const CopyLink = styled.div`
  flex-shrink: 0;
  margin-left: 12px;
  padding: 2px 0 2px 8px;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #fb7e14;
  cursor: pointer;
  user-select: none;
`;

export default Requisites;
